<script>
import { computed, defineAsyncComponent, defineComponent, onMounted, ref, watch } from "vue";
import { inView } from "motion";
import { useDebounceFn, useWindowSize } from "@vueuse/core";
import {
  buildColorStyles,
  buildFlexStyles,
  buildGridStyles,
  buildMarginStyles,
  buildMaxWidthStyles,
  buildPaddingStyles
} from "../../composables/useStyle";
import { findBreakpointSettings } from "../../composables/useBreakpoints";
import { usePageStore } from "../../store/pageStore";
import { useNuxtApp } from "#imports";
const VoixGroupChild = defineAsyncComponent(
  // We have to type as Any due to the recursion and typescript
  () => import("@voix/components/page/VoixGroup.vue")
);
export default defineComponent({
  name: "VoixGroup",
  components: {
    VoixGroupChild
  },
  props: {
    group: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const root = ref();
    const groupStyles = ref();
    const groupWrapperStyles = ref();
    const groupClasses = ref({});
    const pageStore = usePageStore();
    const groupId = computed(() => {
      return `voix-group-${props.group.id}`;
    });
    function generateGroupStyles() {
      const theGroup = Object.assign({}, props.group);
      if (!theGroup)
        return { properties: { settings: {} } };
      let styles = {};
      let wrapperStyles = {};
      if (theGroup.properties.settings) {
        const breakpointSettings = findBreakpointSettings(
          theGroup.properties.settings
        );
        if (breakpointSettings) {
          if (breakpointSettings.position)
            styles.position = breakpointSettings.position;
          if (breakpointSettings.displayMode)
            styles.display = breakpointSettings.displayMode.toString();
          styles = buildFlexStyles(styles, breakpointSettings);
          styles = buildGridStyles(styles, breakpointSettings);
          styles = buildMaxWidthStyles(styles, breakpointSettings);
          wrapperStyles = buildPaddingStyles(wrapperStyles, breakpointSettings);
          wrapperStyles = buildMarginStyles(wrapperStyles, breakpointSettings);
          wrapperStyles = buildColorStyles(wrapperStyles, breakpointSettings);
        }
      }
      groupWrapperStyles.value = wrapperStyles;
      groupStyles.value = styles;
    }
    const updateGroupStyles = useDebounceFn(
      generateGroupStyles,
      10,
      { maxWait: 50 }
    );
    const generateGroupClasses = () => {
    };
    const shouldContain = computed(() => {
      if (!props.group.properties.settings)
        return false;
      const breakpointSettings = findBreakpointSettings(
        props.group.properties.settings
      );
      return breakpointSettings?.contain;
    });
    const { width: windowWidth, height: windowHeight } = useWindowSize();
    watch([windowWidth, windowHeight], () => {
      updateGroupStyles();
    });
    pageStore.$subscribe(() => {
      updateGroupStyles();
    });
    watch(
      props.group,
      (value) => {
        if (value) {
          updateGroupStyles();
          generateGroupClasses();
        }
      },
      { deep: true }
    );
    const setSliceElement = (element) => {
      return element;
    };
    const { $voix } = useNuxtApp();
    const sliceInView = ref(false);
    const countedTheView = ref(false);
    const isContaining = ref(false);
    onMounted(() => {
      updateGroupStyles();
      generateGroupClasses();
      if (shouldContain.value)
        isContaining.value = shouldContain.value;
      inView(root.value, () => {
        if (!countedTheView.value)
          $voix.eventBus.emit("voix:slice:viewed", props.group);
        sliceInView.value = true;
        countedTheView.value = true;
        return () => {
          sliceInView.value = false;
        };
      });
    });
    return {
      props,
      root,
      groupStyles,
      groupWrapperStyles,
      groupClasses,
      setSliceElement,
      isContaining,
      groupId
    };
  }
});
</script>

<template>
  <div :id="groupId" :style="groupWrapperStyles">
    <div :class="{ container: isContaining }">
      <div :id="group.properties.dom_id" ref="root" :style="groupStyles" :class="groupClasses">
        <template v-for="(element, key) in group.elements">
          <VoixSliceLoop v-if="element.type === 'slice'" :key="element.id" :element="setSliceElement(element)" />
          <VoixGroupChild v-if="element.type === 'group'" :key="key" :group="element" />
        </template>
      </div>
    </div>
  </div>
</template>
